import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import { PagePaginationResponseDtoSchema, responseContract } from '../../lib';

export const BedHistoryStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

const BedHistoryAlertDtoSchema = z.object({
  code: z.string(),
  severity: z.enum(['warning', 'info']),
  message: z.string(),
});

export type BedHistoryAlertDto = z.infer<typeof BedHistoryAlertDtoSchema>;

const BedHistoryDtoSchema = z
  .object({
    id: z.number(),
    residentId: z.number(),
    residentName: z.string().optional(),
    resident: z.string().optional(),
    checkInDate: z.string().nullable(),
    checkOutDate: z.string().nullable(),
    status: z
      .string()
      .transform((val) => val.toUpperCase() as 'ACTIVE' | 'INACTIVE'),
    alerts: z.array(BedHistoryAlertDtoSchema).default([]),
  })
  .transform((data) => ({
    ...data,
    residentName: data.residentName || data.resident || 'Unknown',
  }));

const GetBedHistoryResponseDtoSchema =
  PagePaginationResponseDtoSchema(BedHistoryDtoSchema);

export type GetBedHistoryRequestDto = {
  floorPlanId: number;
  page?: number;
  perPage?: number;
  sortBy?: 'residentName' | 'checkInDate' | 'checkOutDate' | 'status';
  sortDirection?: 'asc' | 'desc';
};

export type GetBedHistoryResponseDto = z.infer<
  typeof GetBedHistoryResponseDtoSchema
>;
export type BedHistoryDto = z.infer<typeof BedHistoryDtoSchema>;

export const getBedHistory = (
  params: GetBedHistoryRequestDto,
  config?: AxiosRequestConfig
) => {
  const { floorPlanId, ...restParams } = params;

  return api
    .get(`/floor-plan/${floorPlanId}/history`, {
      params: restParams,
      ...config,
    })
    .then(responseContract(GetBedHistoryResponseDtoSchema));
};
